import { DASHBOARD_PLATFORM_LABELS } from "./dashboardPlatformIcons"
import { formatPlatformLabel } from "./dashboardReportFormatters"
import {
  barPlacement,
  buildGrowthAxis,
  formatGrowthTick,
  growthToneClass,
  isGrowthMetric,
  readPlatformRaw,
} from "./socialPerformanceBenchmarkAxis"

const SP_PLATFORM_COLORS = {
  facebook: "#1877f2",
  instagram: "#e1306c",
  linkedin: "#4f46e5",
  twitter: "#536471",
  tiktok: "#00b4d8",
}

const ALL_PLATFORMS = ["facebook", "instagram", "linkedin", "twitter", "tiktok"]

function escapeHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

function benchmarkLegendPlatformLabel(platform) {
  return DASHBOARD_PLATFORM_LABELS[platform] || formatPlatformLabel(platform)
}

function renderCampaignLegend(items) {
  if (!items.length) return ""
  return `<div class="campaignPerformanceLegend sp-report-legend" role="group" aria-label="Platform legend">${items
    .map(
      (item) =>
        `<span class="campaignPerformanceLegendItem"><span class="campaignPerformanceLegendSwatch" style="background-color:${item.color}" data-legend-color="${item.color}"></span><span class="sp-report-legend-label">${escapeHtml(item.label)}</span></span>`,
    )
    .join("")}</div>`
}

function renderStackedPlatformValues(row, metricKey, platforms) {
  if (!row?.platformMetrics || !platforms.length) {
    const toneClass = isGrowthMetric({ key: metricKey }) ? growthToneClass(row?.metrics?.[metricKey]) : ""
    return `<span class="sp-report-value ${toneClass}">${escapeHtml(row?.metrics?.[metricKey] ?? "0")}</span>`
  }
  return platforms
    .map((platform) => {
      const value = row.platformMetrics?.[platform]?.[metricKey]?.display ?? "0"
      const color = SP_PLATFORM_COLORS[platform] || SP_PLATFORM_COLORS.twitter
      const platformTone = isGrowthMetric({ key: metricKey })
        ? growthToneClass(row.platformMetrics?.[platform]?.[metricKey]?.raw)
        : ""
      return `<span class="sp-report-value ${platformTone}">
        <span class="campaignPerformanceLegendSwatch" style="background:${color}"></span>
        ${escapeHtml(value)}
      </span>`
    })
    .join("")
}

function renderBenchmarkBoard({ rows, metrics, platforms }) {
  const metricDefs = (Array.isArray(metrics) ? metrics : [])
    .map((metric) => (typeof metric === "string" ? { key: metric, label: metric } : metric))
    .filter((metric) => metric?.key)
  const chartMetric = metricDefs.find((metric) => isGrowthMetric(metric)) || metricDefs[0]
  const chartMetricKey = chartMetric?.key
  const chartMetricLabel = chartMetric?.label || "Growth"
  const axis = buildGrowthAxis(
    rows.flatMap((row) => platforms.map((platform) => readPlatformRaw(row, platform, chartMetricKey))),
  )
  const rowCount = Math.max(rows.length, 1)
  const headHeight = rowCount >= 7 ? 88 : 80
  const plotHeight = 112
  const metricHeight = Math.max(28, platforms.length * 12 + 6)
  const tickRange = axis.yMax - axis.yMin || 1

  const plotLines = axis.ticks
    .map((tick) => {
      const top = ((axis.yMax - tick) / tickRange) * 100
      return `<span class="sp-report-plot-line${tick === 0 ? " is-zero" : ""}" style="top:${top}%"></span>`
    })
    .join("")

  const axisTicks = axis.ticks
    .map((tick) => {
      const top = ((axis.yMax - tick) / tickRange) * 100
      return `<span class="sp-report-axis-tick" style="top:${top}%">${escapeHtml(formatGrowthTick(tick))}</span>`
    })
    .join("")

  const metricLabels = metricDefs
    .map((metric) => `<div class="sp-report-metric-label">${escapeHtml(metric.label)}</div>`)
    .join("")

  const columns = rows
    .map((row) => {
      const bars = platforms
        .map((platform) => {
          const raw = readPlatformRaw(row, platform, chartMetricKey)
          const color = SP_PLATFORM_COLORS[platform] || SP_PLATFORM_COLORS.twitter
          const place = barPlacement(raw, axis.yMin, axis.yMax)
          const position = raw < 0 ? `top:${place.top}` : `bottom:${place.bottom}`
          return `<span class="sp-report-bar-track">
            <span class="sp-report-bar${raw < 0 ? " is-negative" : ""}" style="${position};height:${place.height};background:${color}"></span>
          </span>`
        })
        .join("")
      const metricRows = metricDefs
        .map(
          (metric) =>
            `<div class="sp-report-metric-stack">${renderStackedPlatformValues(row, metric.key, platforms)}</div>`,
        )
        .join("")
      const badge = row.isYou
        ? '<span class="sp-report-badge sp-report-badge--you">YOU</span>'
        : row.isBrandAverage
          ? '<span class="sp-report-badge sp-report-badge--avg">Brand Average</span>'
          : ""
      const classes = ["sp-report-col", row.isYou ? "is-you" : "", row.isBrandAverage ? "is-average" : ""]
        .filter(Boolean)
        .join(" ")
      const colStyle = row.isYou
        ? ' style="background-color:#f5f3ff;border-bottom:3px solid #871ed6"'
        : row.isBrandAverage
          ? ' style="background-color:#f9fafb;border-bottom:2px solid #d1d5db"'
          : ""
      return `<div class="${classes}"${colStyle}>
        <div class="sp-report-head">
          <div class="sp-report-rank">#${escapeHtml(row.rank)}</div>
          <div class="sp-report-name">${escapeHtml(row.brand || row.name || "—")}</div>
          <div class="sp-report-badge-slot">${badge}</div>
        </div>
        <div class="sp-report-bars">
          <div class="sp-report-plot-col" aria-hidden="true">${plotLines}</div>
          <div class="sp-report-bar-cluster">${bars}</div>
        </div>
        ${metricRows}
      </div>`
    })
    .join("")

  const legend = renderCampaignLegend(
    platforms.map((platform) => ({
      label: benchmarkLegendPlatformLabel(platform),
      color: SP_PLATFORM_COLORS[platform] || SP_PLATFORM_COLORS.twitter,
    })),
  )

  return `<div class="sp-report" style="--sp-report-cols:${rowCount};--sp-report-metric-count:${metricDefs.length || 3};--sp-report-metric:${metricHeight}px;--sp-report-head:${headHeight}px;--sp-report-plot:${plotHeight}px">
    <div class="sp-report-board">
      <div class="sp-report-pinned">
        <div class="sp-report-head-spacer"></div>
        <div class="sp-report-axis">
          <div class="sp-report-axis-title">${escapeHtml(chartMetricLabel)}</div>
          <div class="sp-report-axis-ticks">${axisTicks}</div>
        </div>
        ${metricLabels}
      </div>
      <div class="sp-report-grid">
        <div class="sp-report-cols">${columns}</div>
      </div>
    </div>
    ${legend}
  </div>`
}

function renderSectionInsight(insight) {
  const text = String(insight ?? "").trim()
  if (!text) return ""
  return `<div class="report-chart-insight" role="note">
    <div class="report-chart-insight__label">Insight</div>
    <p class="report-chart-insight__text">${escapeHtml(text)}</p>
  </div>`
}

function renderBenchmarkView({ title, section, platforms }) {
  const rows = section?.rows || []
  if (!rows.length) {
    return `<div class="report-table-wrap report-table-wrap--competitors report-pdf-block">
      <div class="report-table-title">${escapeHtml(title)}</div>
      <p class="report-no-data">No data available</p>
    </div>`
  }
  const youRow = rows.find((row) => row.isYou)
  const insight =
    section.strategicInsight ||
    (youRow ? `You rank #${youRow.rank} among ${rows.length} brands in the competitive set.` : "")
  return `<div class="report-table-wrap report-table-wrap--competitors report-pdf-block">
    <div class="report-table-title">${escapeHtml(title)}</div>
    ${renderBenchmarkBoard({ rows, metrics: section.metrics || [], platforms })}
    ${renderSectionInsight(insight)}
  </div>`
}

export function resolveBenchmarkExportPlatforms(byTab, connectedPlatformKeys) {
  const fromEngagement = byTab?.engagement?.connectedPlatforms
  const fromGrowth = byTab?.growth?.connectedPlatforms
  const fromSection = Array.isArray(fromEngagement) && fromEngagement.length
    ? fromEngagement
    : Array.isArray(fromGrowth) && fromGrowth.length
      ? fromGrowth
      : []
  if (fromSection.length > 0) {
    return fromSection.filter((platform) => ALL_PLATFORMS.includes(platform))
  }

  const connected = (connectedPlatformKeys || []).filter((platform) => ALL_PLATFORMS.includes(platform))
  if (connected.length > 0) return connected

  const rows = [...(byTab?.engagement?.rows || []), ...(byTab?.growth?.rows || [])]
  const active = new Set()
  for (const row of rows) {
    Object.keys(row.platformMetrics || {}).forEach((platform) => {
      if (ALL_PLATFORMS.includes(platform)) active.add(platform)
    })
  }
  const fromData = ALL_PLATFORMS.filter((platform) => active.has(platform))
  return fromData.length > 0 ? fromData : ["instagram", "facebook"]
}

export function buildSocialPerformanceBenchmarkRichHtml(ctx = {}) {
  const byTab = ctx.socialPerformanceByTab || {}
  const engagement = byTab.engagement || { rows: [], metrics: [] }
  const follower = byTab.growth || { rows: [], metrics: [] }
  const engagementRows = engagement.rows || []
  const followerRows = follower.rows || []

  if (!engagementRows.length && !followerRows.length) return ""

  const platforms = resolveBenchmarkExportPlatforms(byTab, ctx.monthlySocialConnectedPlatformKeys)
  const exportAllTabs = (ctx.exportAllDataChartIds || []).includes("socialPerformanceBenchmark")
  const activeTab = ctx.socialPerformanceTab === "growth" ? "growth" : "engagement"
  const views = []

  if (exportAllTabs || activeTab === "engagement") {
    if (engagementRows.length) {
      views.push(renderBenchmarkView({ title: "Engagement", section: engagement, platforms }))
    }
  }
  if (exportAllTabs || activeTab === "growth") {
    if (followerRows.length) {
      views.push(renderBenchmarkView({ title: "Follower Growth", section: follower, platforms }))
    }
  }

  if (!views.length) return ""
  return `<div class="sp-report-views">${views.join("")}</div>`
}

export const SOCIAL_PERFORMANCE_BENCHMARK_REPORT_STYLES = `
.sp-report-views { display: flex; flex-direction: column; gap: 12px; }
.sp-report-views > .report-table-wrap--competitors { margin-top: 0; page-break-inside: avoid; }
.sp-report-views .report-table-title { margin-bottom: 8px; }
.sp-report-views .report-table-wrap--competitors .report-chart-insight { margin-top: 10px; }
.sp-report { width: 100%; min-width: 0; max-width: 100%; box-sizing: border-box; }
.sp-report-board {
  display: flex; align-items: stretch; width: 100%; min-width: 0; max-width: 100%; box-sizing: border-box;
  overflow: hidden; border: 1px solid var(--soho-border); border-radius: 10px; background: #fff;
}
.sp-report-pinned {
  flex: 0 0 92px; width: 92px; display: flex; flex-direction: column; flex-shrink: 0;
  background: #f4f6f8; border-right: 1px solid var(--soho-border);
}
.sp-report-head-spacer {
  flex: 0 0 var(--sp-report-head, 80px); height: var(--sp-report-head, 80px); box-sizing: border-box;
}
.sp-report-axis {
  display: flex; flex-direction: column; gap: 4px; flex: 0 0 var(--sp-report-plot, 112px);
  height: var(--sp-report-plot, 112px); box-sizing: border-box; padding: 8px 8px 6px 10px;
}
.sp-report-axis-title {
  width: 100%; font-size: 6pt; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--soho-muted); line-height: 1.25; word-break: break-word; overflow-wrap: anywhere;
}
.sp-report-axis-ticks { position: relative; flex: 1; min-height: 0; width: 100%; }
.sp-report-axis-tick {
  position: absolute; right: 4px; transform: translateY(-50%); font-size: 6.5pt; font-weight: 600;
  color: var(--soho-muted); font-variant-numeric: tabular-nums; white-space: nowrap; line-height: 1;
}
.sp-report-metric-label {
  display: flex; align-items: center; flex: 0 0 var(--sp-report-metric, 32px);
  height: var(--sp-report-metric, 32px); box-sizing: border-box;
  padding: 0 8px; font-size: 6pt; font-weight: 700;
  letter-spacing: 0.03em; text-transform: uppercase; color: var(--soho-muted); line-height: 1.2;
  border-top: 1px solid #e8ecf1; word-break: break-word; overflow-wrap: anywhere;
}
.sp-report-grid {
  position: relative; flex: 1 1 0; min-width: 0; width: 100%; overflow: hidden;
}
.sp-report-cols {
  display: flex; align-items: stretch; width: 100%; min-width: 0; position: relative;
}
.sp-report-plot-col {
  position: absolute; left: 0; right: 0; top: 8px; bottom: 8px;
  pointer-events: none; z-index: 0;
}
.sp-report-plot-line { position: absolute; left: 0; right: 0; border-top: 1px solid #e8ecf1; }
.sp-report-plot-line.is-zero { border-top-color: #d1d5db; }
.sp-report-col {
  display: flex; flex-direction: column; flex: 1 1 0; min-width: 0; width: 0;
  position: relative; overflow: hidden; box-sizing: border-box;
}
.sp-report-col.is-you {
  background-color: #f5f3ff !important;
  box-shadow: none !important;
  border-bottom: 3px solid #871ed6;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.sp-report-col.is-you .sp-report-head,
.sp-report-col.is-you .sp-report-metric-stack {
  background-color: #f5f3ff !important;
}
.sp-report-col.is-you .sp-report-metric-stack {
  border-top-color: #ede9fe;
}
.sp-report-badge-slot {
  min-height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sp-report-badge {
  font-size: 5.5pt;
  font-weight: 700;
  letter-spacing: 0.3px;
  padding: 1px 6px;
  border-radius: 999px;
  white-space: nowrap;
  line-height: 1.3;
  text-transform: uppercase;
}
.sp-report-badge--you {
  background-color: #871ed6 !important;
  color: #ffffff !important;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.sp-report-badge--avg {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  text-transform: none;
}
.sp-report-col.is-average {
  background-color: #f9fafb !important;
  box-shadow: none !important;
  border-bottom: 2px solid #d1d5db;
}
.sp-report-head {
  display: flex; flex-direction: column; align-items: center; justify-content: flex-start; gap: 2px;
  flex: 0 0 var(--sp-report-head, 80px); height: var(--sp-report-head, 80px);
  padding: 6px 3px 4px; text-align: center; min-width: 0; box-sizing: border-box;
}
.sp-report-rank { font-size: 7pt; font-weight: 600; color: var(--soho-muted); line-height: 1; flex-shrink: 0; }
.sp-report-name {
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; overflow: hidden; width: 100%;
  font-size: 6.75pt; font-weight: 700; color: var(--soho-ink); line-height: 1.2;
  overflow-wrap: anywhere; word-break: break-word;
}
.sp-report-bars {
  position: relative; flex: 0 0 var(--sp-report-plot, 112px); height: var(--sp-report-plot, 112px);
  min-height: 0; padding: 8px 0; box-sizing: border-box; overflow: hidden;
}
.sp-report-bar-cluster {
  position: relative; z-index: 1; height: 100%; display: flex; justify-content: center; gap: 2px; padding: 0 3px;
}
.sp-report-bar-track { position: relative; width: 7px; height: 100%; flex-shrink: 0; }
.sp-report-bar { position: absolute; left: 0; width: 7px; border-radius: 2px 2px 0 0; min-height: 2px; }
.sp-report-bar.is-negative { border-radius: 0 0 2px 2px; }
.sp-report-metric-stack {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px;
  flex: 0 0 var(--sp-report-metric, 36px); height: var(--sp-report-metric, 36px);
  padding: 3px 2px; border-top: 1px solid #f3f4f6; min-width: 0; box-sizing: border-box;
}
.sp-report-value {
  display: inline-flex; align-items: center; gap: 2px; font-size: 6.5pt; font-weight: 600; color: #374151;
  font-variant-numeric: tabular-nums; line-height: 1.2; max-width: 100%; white-space: normal; text-align: center;
}
.sp-report-col.is-you .sp-report-value:not(.is-up):not(.is-down) { color: #6b21a8; }
.sp-report-value.is-up, .sp-report-col.is-you .sp-report-value.is-up { color: #059669; }
.sp-report-value.is-down, .sp-report-col.is-you .sp-report-value.is-down { color: #dc2626; }
.sp-report-value .campaignPerformanceLegendSwatch { width: 5px; height: 5px; flex-shrink: 0; }
.sp-report .campaignPerformanceLegend, .sp-report-legend {
  margin-top: 10px; margin-bottom: 0; padding-top: 2px; font-size: 7.5pt; display: flex; flex-wrap: wrap; justify-content: center; gap: 18px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.sp-report-legend .campaignPerformanceLegendItem {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--soho-ink);
  font-weight: 600;
  line-height: 1;
}
.sp-report-legend .campaignPerformanceLegendSwatch {
  width: 10px;
  height: 10px;
  min-width: 10px;
  min-height: 10px;
  border-radius: 2px;
  display: inline-block;
  flex-shrink: 0;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
`
